import dayjs from "dayjs";
import {
  AlertTriangle,
  ArrowRight,
  Ban,
  Boxes,
  Building2,
  Calendar,
  CheckCircle,
  Droplet,
  Factory,
  Hash,
  History,
  MapPin,
  Package,
  QrCode,
  User,
} from "lucide-react";

import { Alert, AlertDescription } from "~/components/ui/alert";
import { Badge } from "~/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import { Skeleton } from "~/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import {
  useOilTypeStockByPlant,
  useRawMaterialStockByPlant,
} from "~/hooks/purchasing/useStockByPlant";
import { useReturnableTotemFleetSummary } from "~/pages/totems/hooks/useTotems";
import { TOTEM_BUCKETS } from "~/pages/totems/utils/buckets";
import { MOVEMENT_TYPE_LABELS, PALLET_STATUS_LABELS } from "~/types/wms";

import type {
  ProductStockDetailData,
  ProductStockMovementRow,
} from "~/hooks/wms";
import type {
  LotTraceabilityRow,
  MovementType,
  PalletMovementHistoryRow,
  PalletSearchResult,
  PalletStatus,
  PositionStatusResult,
  ProductInventoryDetail,
  StockSummary,
} from "~/types/wms";

// ============== LIVE PRODUCT STOCK DETAIL ==============
// Shows the FÍSICO (bodega) balance from products.stock plus the derived
// "facturado no despachado" and "disponible para la venta" (físico − no despachado).
// products.stock is the single source of truth: production increments it, DISPATCH
// (in_transit/delivered) decrements it. Movements come from the stock_movements ledger.

const STOCK_MOVEMENT_LABELS: Record<string, string> = {
  production: "Producción",
  consumption: "Despacho",
  adjustment: "Ajuste",
  reservation: "Reserva",
  release: "Liberación",
  info: "Info",
};

const stockNumberFormat = new Intl.NumberFormat("es-CL");

interface ProductStockDetailProps {
  detail: ProductStockDetailData | null | undefined;
  movements: ProductStockMovementRow[];
  isLoading: boolean;
}

export function ProductStockDetail({
  detail,
  movements,
  isLoading,
}: ProductStockDetailProps) {
  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-28" />
        <Skeleton className="h-64" />
      </div>
    );
  }

  const stock = detail?.stock ?? 0; // físico (bodega)
  const reserved = detail?.reserved ?? 0; // facturado no despachado
  const available = detail?.available ?? stock - reserved; // disponible para la venta
  const unit = detail?.unit;
  const isNegative = stock < 0;

  return (
    <>
      {/* Físico headline + derived breakdown */}
      <Card>
        <CardContent className="py-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Stock físico (bodega)</p>
              <p
                className={`text-3xl font-bold ${isNegative ? "text-red-700" : ""}`}
              >
                {stockNumberFormat.format(stock)}
                {unit && (
                  <span className="ml-2 text-base font-normal text-muted-foreground">
                    {unit}
                  </span>
                )}
              </p>
            </div>
            <Boxes
              className={`h-10 w-10 ${isNegative ? "text-red-400" : "text-muted-foreground/40"}`}
            />
          </div>
          <div className="mt-4 grid grid-cols-2 gap-4 border-t pt-4">
            <div>
              <p className="text-xs text-muted-foreground">
                Facturado no despachado
              </p>
              <p className="text-lg font-semibold">
                {stockNumberFormat.format(reserved)}
                {unit && (
                  <span className="ml-1 text-xs font-normal text-muted-foreground">
                    {unit}
                  </span>
                )}
              </p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">
                Disponible para la venta
              </p>
              <p
                className={`text-lg font-semibold ${available < 0 ? "text-red-700" : ""}`}
              >
                {stockNumberFormat.format(available)}
                {unit && (
                  <span className="ml-1 text-xs font-normal text-muted-foreground">
                    {unit}
                  </span>
                )}
              </p>
            </div>
          </div>
          {isNegative && (
            <p className="mt-3 text-xs text-red-600">
              Stock físico negativo: se despachó más de lo disponible. Revisa el
              conteo físico o los despachos del período.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Recent movements */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <History className="h-4 w-4" />
            Movimientos recientes
          </CardTitle>
        </CardHeader>
        <CardContent className={movements.length === 0 ? "" : "p-0"}>
          {movements.length === 0 ? (
            <div className="py-8 text-center text-muted-foreground">
              <History className="mx-auto h-12 w-12 opacity-30" />
              <p className="mt-2">No hay movimientos registrados</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Fecha</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead className="text-right">Cantidad</TableHead>
                  <TableHead>Motivo</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {movements.map((m, index) => (
                  <TableRow
                    key={m.id}
                    style={{ animationDelay: `${Math.min(index * 40, 400)}ms` }}
                    className="duration-300 animate-in fade-in-0 slide-in-from-top-1 fill-mode-both"
                  >
                    <TableCell>
                      <div className="text-sm">
                        {dayjs(m.created_at).format("DD/MM/YYYY")}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {dayjs(m.created_at).format("HH:mm")}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        {STOCK_MOVEMENT_LABELS[m.movement_type] ||
                          m.movement_type}
                      </Badge>
                    </TableCell>
                    <TableCell
                      className={`text-right font-mono font-medium ${
                        m.quantity_change < 0
                          ? "text-red-700"
                          : m.quantity_change > 0
                            ? "text-green-700"
                            : ""
                      }`}
                    >
                      {m.quantity_change > 0 ? "+" : ""}
                      {stockNumberFormat.format(m.quantity_change)}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {m.reason || "-"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </>
  );
}

// ============== PRODUCT DETAIL VIEW ==============

interface ProductDetailViewProps {
  stockSummary: StockSummary | null | undefined;
  inventoryDetails: ProductInventoryDetail[];
  isLoading: boolean;
}

export function ProductDetailView({
  stockSummary,
  inventoryDetails,
  isLoading,
}: ProductDetailViewProps) {
  const hasTransicionStock = stockSummary && stockSummary.transicion_boxes > 0;

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-3">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-24" />
          ))}
        </div>
        <Skeleton className="h-64" />
      </div>
    );
  }

  return (
    <>
      {/* Stock Summary Cards */}
      {stockSummary && (
        <>
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-3">
            <Card className="border-green-200 bg-green-50">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-green-700">Disponible</p>
                    <p className="text-2xl font-bold text-green-700">
                      {stockSummary.wms_available_boxes}
                    </p>
                  </div>
                  <CheckCircle className="h-8 w-8 text-green-500" />
                </div>
              </CardContent>
            </Card>

            <Card className="border-red-200 bg-red-50">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-red-700">Bloqueado</p>
                    <p className="text-2xl font-bold text-red-700">
                      {stockSummary.blocked_by_expiration}
                    </p>
                    <p className="mt-1 text-xs text-red-600">
                      Por vencimiento (&lt;30 días)
                    </p>
                  </div>
                  <Ban className="h-8 w-8 text-red-500" />
                </div>
              </CardContent>
            </Card>

            <Card className="border-blue-200 bg-blue-50">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-blue-700">Total WMS</p>
                    <p className="text-2xl font-bold text-blue-700">
                      {stockSummary.wms_total_boxes}
                    </p>
                  </div>
                  <Boxes className="h-8 w-8 text-blue-500" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Stock en Bodega vs Transición */}
          <Card>
            <CardContent className="py-4">
              <div className="flex flex-wrap items-center justify-center gap-6">
                <div className="text-center">
                  <p className="text-sm text-muted-foreground">
                    Stock en Bodega
                  </p>
                  <p className="text-xl font-bold">
                    {stockSummary.wms_total_boxes}
                  </p>
                </div>
                <div className="text-2xl text-muted-foreground">+</div>
                <div className="text-center">
                  <p className="text-sm text-muted-foreground">
                    Stock en Transición
                  </p>
                  <p className="text-xl font-bold text-amber-600">
                    {stockSummary.transicion_boxes}
                  </p>
                </div>
                <div className="text-2xl text-muted-foreground">=</div>
                <div className="text-center">
                  <p className="text-sm text-muted-foreground">Total WMS</p>
                  <p className="text-xl font-bold">
                    {stockSummary.wms_total_boxes +
                      stockSummary.transicion_boxes}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Per-warehouse breakdown */}
          {inventoryDetails.length > 0 &&
            (() => {
              const byWarehouse = inventoryDetails.reduce<
                Record<string, { boxes: number; available: number }>
              >((acc, inv) => {
                const key = inv.warehouse_name || "Sin bodega";
                acc[key] = acc[key] ?? { boxes: 0, available: 0 };
                acc[key].boxes += inv.box_count;
                acc[key].available += inv.available_boxes;
                return acc;
              }, {});
              const warehouses = Object.entries(byWarehouse);
              if (warehouses.length <= 1) return null;
              return (
                <div
                  className={`grid gap-4 ${warehouses.length === 2 ? "grid-cols-2" : "grid-cols-2 md:grid-cols-3"}`}
                >
                  {warehouses.map(([name, totals]) => (
                    <Card key={name}>
                      <CardContent className="pt-5">
                        <div className="flex items-center gap-3">
                          <div className="rounded-lg bg-slate-100 p-2">
                            <Building2 className="h-5 w-5 text-slate-600" />
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">
                              {name}
                            </p>
                            <p className="text-xl font-bold">{totals.boxes}</p>
                            <p className="text-xs text-green-600">
                              {totals.available} disp.
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              );
            })()}

          {hasTransicionStock && (
            <Alert>
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                Hay {stockSummary.transicion_boxes} cajas en bodega de
                transición pendientes de ingreso al WMS.
              </AlertDescription>
            </Alert>
          )}
        </>
      )}

      {/* Inventory Detail Table */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <MapPin className="h-4 w-4" />
            Detalle por Ubicación
          </CardTitle>
        </CardHeader>
        <CardContent>
          {inventoryDetails.length === 0 ? (
            <div className="py-8 text-center text-muted-foreground">
              <MapPin className="mx-auto h-12 w-12 opacity-30" />
              <p className="mt-2">
                No hay inventario en WMS para este producto
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Ubicación</TableHead>
                  <TableHead>Bodega</TableHead>
                  <TableHead>Pallet</TableHead>
                  <TableHead>Lote</TableHead>
                  <TableHead className="text-right">Cajas</TableHead>
                  <TableHead className="text-right">Disp.</TableHead>
                  <TableHead>Vencimiento</TableHead>
                  <TableHead>Estado</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {inventoryDetails.map((inv, index) => (
                  <TableRow
                    key={inv.inventory_id}
                    style={{ animationDelay: `${Math.min(index * 40, 400)}ms` }}
                    className="duration-300 animate-in fade-in-0 slide-in-from-top-1 fill-mode-both"
                  >
                    <TableCell>
                      <span className="font-mono text-sm">
                        {inv.position_code}
                      </span>
                    </TableCell>
                    <TableCell>{inv.warehouse_name}</TableCell>
                    <TableCell>
                      {inv.pallet_barcode ? (
                        <span className="font-mono text-xs">
                          {inv.pallet_barcode}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {inv.lot_number || (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right font-medium">
                      {inv.box_count}
                    </TableCell>
                    <TableCell className="text-right font-medium text-green-600">
                      {inv.available_boxes}
                    </TableCell>
                    <TableCell>
                      {inv.expiration_date ? (
                        <div className="flex items-center gap-1">
                          <Calendar className="h-3 w-3 text-muted-foreground" />
                          <span
                            className={
                              inv.days_until_expiry !== null &&
                              inv.days_until_expiry <= 30
                                ? "font-medium text-red-600"
                                : ""
                            }
                          >
                            {dayjs(inv.expiration_date).format("DD/MM/YYYY")}
                          </span>
                          {inv.days_until_expiry !== null && (
                            <span className="text-xs text-muted-foreground">
                              ({inv.days_until_expiry}d)
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {inv.is_blocked ? (
                        <Badge variant="destructive" className="text-xs">
                          <Ban className="mr-1 h-3 w-3" />
                          Bloqueado
                        </Badge>
                      ) : (
                        <Badge
                          variant="outline"
                          className="border-green-300 bg-green-50 text-green-700"
                        >
                          <CheckCircle className="mr-1 h-3 w-3" />
                          OK
                        </Badge>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </>
  );
}

// ============== PALLET DETAIL VIEW ==============

interface PalletDetailViewProps {
  pallet: PalletSearchResult;
  movementHistory: PalletMovementHistoryRow[];
  isLoading: boolean;
}

const statusConfig: Record<
  string,
  {
    variant: "default" | "secondary" | "destructive" | "outline";
    color: string;
  }
> = {
  in_production: { variant: "secondary", color: "text-gray-600" },
  ready_for_ingestion: { variant: "default", color: "text-blue-600" },
  in_warehouse: { variant: "default", color: "text-green-600" },
  partial: { variant: "default", color: "text-amber-600" },
  depleted: { variant: "secondary", color: "text-gray-400" },
  empty: { variant: "secondary", color: "text-gray-400" },
};

export function PalletDetailView({
  pallet,
  movementHistory,
  isLoading,
}: PalletDetailViewProps) {
  const config = statusConfig[pallet.status] || statusConfig.in_warehouse;

  return (
    <>
      {/* Pallet Status Card */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div>
              <div className="flex items-center gap-3">
                <QrCode className="h-6 w-6 text-muted-foreground" />
                <span className="font-mono text-xl font-bold">
                  {pallet.pallet_code}
                </span>
                <Badge variant={config.variant}>
                  {PALLET_STATUS_LABELS[pallet.status as PalletStatus] ||
                    pallet.status}
                </Badge>
              </div>
              {pallet.position_code && (
                <div className="mt-2 flex items-center gap-2 text-sm text-muted-foreground">
                  <MapPin className="h-4 w-4" />
                  <span>Ubicación: {pallet.position_code}</span>
                  {pallet.warehouse_name && (
                    <span>| {pallet.warehouse_name}</span>
                  )}
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Pallet Content */}
      {pallet.product_name && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Package className="h-4 w-4" />
              Contenido del Pallet
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <p className="text-sm text-muted-foreground">Producto</p>
                <p className="font-medium">{pallet.product_name}</p>
                {pallet.sku && (
                  <Badge variant="secondary" className="mt-1">
                    SKU: {pallet.sku}
                  </Badge>
                )}
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Cajas</p>
                <p className="text-2xl font-bold">{pallet.current_box_count}</p>
                <p className="text-xs text-muted-foreground">
                  Inicial: {pallet.initial_box_count} | Capacidad:{" "}
                  {pallet.capacity}
                </p>
              </div>
              {pallet.lot_number && (
                <div>
                  <p className="text-sm text-muted-foreground">Lote</p>
                  <p className="font-mono">{pallet.lot_number}</p>
                </div>
              )}
              {pallet.expiration_date && (
                <div>
                  <p className="text-sm text-muted-foreground">Vencimiento</p>
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    <span>
                      {dayjs(pallet.expiration_date).format("DD/MM/YYYY")}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Movement History */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <History className="h-4 w-4" />
            Historial de Movimientos
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-2">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : movementHistory.length === 0 ? (
            <div className="py-8 text-center text-muted-foreground">
              <History className="mx-auto h-12 w-12 opacity-30" />
              <p className="mt-2">No hay movimientos registrados</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Fecha</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead className="text-right">Cajas</TableHead>
                  <TableHead>Movimiento</TableHead>
                  <TableHead>Usuario</TableHead>
                  <TableHead>Notas</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {movementHistory.map((movement, index) => (
                  <TableRow
                    key={movement.movement_id}
                    style={{ animationDelay: `${Math.min(index * 40, 400)}ms` }}
                    className="duration-300 animate-in fade-in-0 slide-in-from-top-1 fill-mode-both"
                  >
                    <TableCell>
                      <div className="text-sm">
                        {dayjs(movement.created_at).format("DD/MM/YYYY")}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {dayjs(movement.created_at).format("HH:mm")}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        {MOVEMENT_TYPE_LABELS[
                          movement.movement_type as MovementType
                        ] || movement.movement_type}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right font-medium">
                      {movement.quantity > 0
                        ? `+${movement.quantity}`
                        : movement.quantity}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 text-sm">
                        <span className="font-mono text-xs">
                          {movement.from_position || "-"}
                        </span>
                        <ArrowRight className="h-3 w-3 text-muted-foreground" />
                        <span className="font-mono text-xs">
                          {movement.to_position || "-"}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      {movement.performed_by_name ? (
                        <div className="flex items-center gap-1 text-sm">
                          <User className="h-3 w-3 text-muted-foreground" />
                          <span>{movement.performed_by_name}</span>
                        </div>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-muted-foreground">
                        {movement.notes || "-"}
                      </span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </>
  );
}

// ============== LOT DETAIL VIEW ==============

interface LotDetailViewProps {
  lotNumber: string;
  lotData: LotTraceabilityRow[];
}

export function LotDetailView({ lotNumber, lotData }: LotDetailViewProps) {
  // Calculate summary stats
  const totalBoxes = lotData.reduce((sum, row) => sum + row.box_count, 0);
  const totalAvailable = lotData.reduce(
    (sum, row) => sum + row.available_boxes,
    0
  );
  const uniquePositions = new Set(lotData.map((row) => row.position_code)).size;
  const uniqueProducts = new Set(lotData.map((row) => row.product_id)).size;

  // Get earliest expiration
  const expirationDates = lotData
    .filter((row) => row.expiration_date)
    .map((row) => ({
      date: row.expiration_date,
      days: row.days_until_expiry,
    }));
  const earliestExpiration =
    expirationDates.length > 0
      ? expirationDates.sort(
          (a, b) => new Date(a.date!).getTime() - new Date(b.date!).getTime()
        )[0]
      : null;

  if (lotData.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <Package className="mx-auto h-12 w-12 text-muted-foreground opacity-30" />
          <p className="mt-4 text-muted-foreground">
            No se encontró inventario para el lote "{lotNumber}"
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      {/* Lot Summary */}
      {earliestExpiration &&
        earliestExpiration.days !== null &&
        earliestExpiration.days <= 30 && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              Este lote tiene producto que vence en {earliestExpiration.days}{" "}
              días ({dayjs(earliestExpiration.date).format("DD/MM/YYYY")})
            </AlertDescription>
          </Alert>
        )}

      {/* Summary Stats */}
      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <Boxes className="mx-auto h-8 w-8 text-blue-500" />
              <p className="mt-2 text-2xl font-bold">{totalBoxes}</p>
              <p className="text-sm text-muted-foreground">Total Cajas</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <CheckCircle className="mx-auto h-8 w-8 text-green-500" />
              <p className="mt-2 text-2xl font-bold">{totalAvailable}</p>
              <p className="text-sm text-muted-foreground">Disponibles</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <MapPin className="mx-auto h-8 w-8 text-purple-500" />
              <p className="mt-2 text-2xl font-bold">{uniquePositions}</p>
              <p className="text-sm text-muted-foreground">Ubicaciones</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <Package className="mx-auto h-8 w-8 text-amber-500" />
              <p className="mt-2 text-2xl font-bold">{uniqueProducts}</p>
              <p className="text-sm text-muted-foreground">Productos</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Lot Detail Table */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Hash className="h-4 w-4" />
            Detalle del Lote
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Ubicación</TableHead>
                <TableHead>Bodega</TableHead>
                <TableHead>Producto</TableHead>
                <TableHead>Pallet</TableHead>
                <TableHead className="text-right">Cajas</TableHead>
                <TableHead className="text-right">Disp.</TableHead>
                <TableHead>Vencimiento</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {lotData.map((row, idx) => (
                <TableRow
                  key={`${row.inventory_id}-${idx}`}
                  style={{ animationDelay: `${Math.min(idx * 40, 400)}ms` }}
                  className="duration-300 animate-in fade-in-0 slide-in-from-top-1 fill-mode-both"
                >
                  <TableCell>
                    <span className="font-mono text-sm">
                      {row.position_code}
                    </span>
                  </TableCell>
                  <TableCell>{row.warehouse_name}</TableCell>
                  <TableCell>
                    <div>
                      <p className="font-medium">{row.product_name}</p>
                      {row.sku && (
                        <Badge variant="secondary" className="mt-1 text-xs">
                          {row.sku}
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    {row.pallet_barcode ? (
                      <span className="font-mono text-xs">
                        {row.pallet_barcode}
                      </span>
                    ) : (
                      <span className="text-muted-foreground">-</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right font-medium">
                    {row.box_count}
                  </TableCell>
                  <TableCell className="text-right font-medium text-green-600">
                    {row.available_boxes}
                  </TableCell>
                  <TableCell>
                    {row.expiration_date ? (
                      <div className="flex items-center gap-1">
                        <Calendar className="h-3 w-3 text-muted-foreground" />
                        <span
                          className={
                            row.days_until_expiry !== null &&
                            row.days_until_expiry <= 30
                              ? "font-medium text-red-600"
                              : ""
                          }
                        >
                          {dayjs(row.expiration_date).format("DD/MM/YYYY")}
                        </span>
                        {row.days_until_expiry !== null && (
                          <span className="text-xs text-muted-foreground">
                            ({row.days_until_expiry}d)
                          </span>
                        )}
                      </div>
                    ) : (
                      <span className="text-muted-foreground">-</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </>
  );
}

// ============== POSITION DETAIL VIEW ==============

interface PositionDetailViewProps {
  positionStatus: PositionStatusResult[] | undefined;
  isLoading: boolean;
}

export function PositionDetailView({
  positionStatus,
  isLoading,
}: PositionDetailViewProps) {
  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-32" />
        <Skeleton className="h-48" />
      </div>
    );
  }

  // Ensure positionStatus is always an array (handles cached data from old format)
  const statusArray = Array.isArray(positionStatus) ? positionStatus : [];

  if (statusArray.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <MapPin className="mx-auto h-12 w-12 text-muted-foreground opacity-30" />
          <p className="mt-4 text-muted-foreground">Esta posición está vacía</p>
        </CardContent>
      </Card>
    );
  }

  // Get position info from first row (all rows have same position data)
  const positionInfo = statusArray[0];
  const isOccupied = statusArray.length > 0;

  // Calculate summary stats
  const totalPallets = statusArray.length;
  const totalBoxes = statusArray.reduce((sum, row) => sum + row.box_count, 0);
  const uniqueProducts = new Set(statusArray.map((row) => row.product_id)).size;

  return (
    <>
      {/* Position Status Card */}
      <Card className={isOccupied ? "border-green-200 bg-green-50" : ""}>
        <CardContent className="p-4 sm:pt-6">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                <MapPin
                  className={`h-5 w-5 sm:h-6 sm:w-6 ${isOccupied ? "text-green-600" : "text-gray-400"}`}
                />
                <span className="font-mono text-lg font-bold sm:text-xl">
                  {positionInfo.position_code}
                </span>
                <Badge variant={isOccupied ? "default" : "secondary"}>
                  {isOccupied ? "Ocupada" : "Vacía"}
                </Badge>
              </div>
              <div className="mt-2 text-xs text-muted-foreground sm:text-sm">
                {positionInfo.warehouse_name} | Pasillo{" "}
                {positionInfo.hallway_code} | Rack {positionInfo.rack_code} |
                Nivel {positionInfo.level_num}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Summary Stats */}
      {isOccupied && (
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-3 sm:gap-4">
          <Card>
            <CardContent className="p-3 sm:pt-6">
              <div className="text-center">
                <QrCode className="mx-auto h-6 w-6 text-purple-500 sm:h-8 sm:w-8" />
                <p className="mt-1 text-xl font-bold sm:mt-2 sm:text-2xl">
                  {totalPallets}
                </p>
                <p className="text-xs text-muted-foreground sm:text-sm">
                  Pallets
                </p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-3 sm:pt-6">
              <div className="text-center">
                <Boxes className="mx-auto h-6 w-6 text-blue-500 sm:h-8 sm:w-8" />
                <p className="mt-1 text-xl font-bold sm:mt-2 sm:text-2xl">
                  {totalBoxes}
                </p>
                <p className="text-xs text-muted-foreground sm:text-sm">
                  Cajas
                </p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-3 sm:pt-6">
              <div className="text-center">
                <Package className="mx-auto h-6 w-6 text-amber-500 sm:h-8 sm:w-8" />
                <p className="mt-1 text-xl font-bold sm:mt-2 sm:text-2xl">
                  {uniqueProducts}
                </p>
                <p className="text-xs text-muted-foreground sm:text-sm">
                  Productos
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Position Content Table */}
      {isOccupied && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-sm sm:text-base">
              <Package className="h-4 w-4" />
              Contenido de la Posición
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0 sm:p-6">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Pallet</TableHead>
                    <TableHead>Producto</TableHead>
                    <TableHead>Lote</TableHead>
                    <TableHead className="text-right">Cajas</TableHead>
                    <TableHead>Vencimiento</TableHead>
                    <TableHead>Estado</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {positionStatus.map((row, index) => (
                    <TableRow
                      key={row.pallet_id}
                      style={{
                        animationDelay: `${Math.min(index * 40, 400)}ms`,
                      }}
                      className="duration-300 animate-in fade-in-0 slide-in-from-top-1 fill-mode-both"
                    >
                      <TableCell>
                        <span className="font-mono text-sm">
                          {row.pallet_barcode}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium">{row.product_name}</p>
                          {row.sku && (
                            <Badge variant="secondary" className="mt-1 text-xs">
                              {row.sku}
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        {row.lot_number || (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {row.box_count}
                      </TableCell>
                      <TableCell>
                        {row.expiration_date ? (
                          <div className="flex items-center gap-1">
                            <Calendar className="h-3 w-3 text-muted-foreground" />
                            <span
                              className={
                                row.days_until_expiry !== null &&
                                row.days_until_expiry <= 30
                                  ? "font-medium text-red-600"
                                  : ""
                              }
                            >
                              {dayjs(row.expiration_date).format("DD/MM/YYYY")}
                            </span>
                            {row.days_until_expiry !== null && (
                              <span className="text-xs text-muted-foreground">
                                ({row.days_until_expiry}d)
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">
                          {PALLET_STATUS_LABELS[
                            row.pallet_status as PalletStatus
                          ] || row.pallet_status}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}
    </>
  );
}

// ============== INGREDIENT DETAIL VIEW ==============

interface IngredientDetailViewProps {
  type: "oil" | "raw_material";
  data: unknown;
}

export function IngredientDetailView({
  type,
  data,
}: IngredientDetailViewProps) {
  const { data: oilStockByPlant } = useOilTypeStockByPlant();
  const { data: rawMaterialStockByPlant } = useRawMaterialStockByPlant();

  // Returnable totems (an insumo) don't live in P1/P2 plants — their physical fleet is
  // tracked by lifecycle state in Gestión de Tótems. Detect via the flag and read the
  // status breakdown so distribution reflects real location (cliente/lavado/merma/...).
  const rawMaterialData =
    type === "raw_material"
      ? (data as { is_returnable_totem?: boolean })
      : null;
  const isReturnableTotem = rawMaterialData?.is_returnable_totem === true;
  const { data: fleetSummary = [] } =
    useReturnableTotemFleetSummary(isReturnableTotem);

  if (type === "oil") {
    const oil = data as {
      id: string;
      name: string;
      code: string;
      current_stock: number;
      minimum_stock_level: number | null;
      density_kg_per_liter: number;
    };

    const stockInTons = Number(oil.current_stock) / 1000;
    const minStockInTons = oil.minimum_stock_level
      ? Number(oil.minimum_stock_level) / 1000
      : null;
    const isLowStock = minStockInTons !== null && stockInTons < minStockInTons;

    const p1Liters = oilStockByPlant?.get(oil.id)?.P1 ?? null;
    const p2Liters = oilStockByPlant?.get(oil.id)?.P2 ?? null;

    return (
      <>
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          <Card
            className={
              isLowStock
                ? "border-red-200 bg-red-50"
                : "border-blue-200 bg-blue-50"
            }
          >
            <CardContent className="pt-6">
              <div className="text-center">
                <Droplet
                  className={`mx-auto h-8 w-8 ${isLowStock ? "text-red-500" : "text-blue-500"}`}
                />
                <p className="mt-2 text-2xl font-bold">
                  {stockInTons.toFixed(2)}
                </p>
                <p className="text-sm text-muted-foreground">
                  Stock Total (ton)
                </p>
              </div>
            </CardContent>
          </Card>

          {p1Liters !== null && (
            <Card className="border-emerald-200 bg-emerald-50">
              <CardContent className="pt-6">
                <div className="text-center">
                  <Factory className="mx-auto h-8 w-8 text-emerald-600" />
                  <p className="mt-2 text-2xl font-bold text-emerald-700">
                    {(p1Liters / 1000).toFixed(2)}
                  </p>
                  <p className="text-sm text-emerald-600">Planta 1 (ton)</p>
                </div>
              </CardContent>
            </Card>
          )}

          {p2Liters !== null && (
            <Card className="border-violet-200 bg-violet-50">
              <CardContent className="pt-6">
                <div className="text-center">
                  <Factory className="mx-auto h-8 w-8 text-violet-600" />
                  <p className="mt-2 text-2xl font-bold text-violet-700">
                    {(p2Liters / 1000).toFixed(2)}
                  </p>
                  <p className="text-sm text-violet-600">Planta 2 (ton)</p>
                </div>
              </CardContent>
            </Card>
          )}

          {minStockInTons !== null && (
            <Card>
              <CardContent className="pt-6">
                <div className="text-center">
                  <AlertTriangle className="mx-auto h-8 w-8 text-amber-500" />
                  <p className="mt-2 text-2xl font-bold">
                    {minStockInTons.toFixed(2)}
                  </p>
                  <p className="text-sm text-muted-foreground">Mínimo (ton)</p>
                </div>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <Droplet className="mx-auto h-8 w-8 text-purple-500" />
                <p className="mt-2 text-2xl font-bold">
                  {oil.density_kg_per_liter}
                </p>
                <p className="text-sm text-muted-foreground">Densidad (kg/L)</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {isLowStock && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              Stock bajo el nivel mínimo. Se recomienda reabastecer.
            </AlertDescription>
          </Alert>
        )}
      </>
    );
  }

  // Raw material
  const material = data as {
    id: string;
    name: string;
    code: string;
    current_stock: number;
    minimum_stock_level: number | null;
    unit: string;
    is_returnable_totem?: boolean;
    group_description?: string | null;
  };

  const isLowStock =
    !isReturnableTotem &&
    material.minimum_stock_level !== null &&
    Number(material.current_stock) < Number(material.minimum_stock_level);

  const p1Stock = rawMaterialStockByPlant?.get(material.id)?.P1 ?? null;
  const p2Stock = rawMaterialStockByPlant?.get(material.id)?.P2 ?? null;

  return (
    <>
      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        <Card
          className={
            isLowStock
              ? "border-red-200 bg-red-50"
              : "border-blue-200 bg-blue-50"
          }
        >
          <CardContent className="pt-6">
            <div className="text-center">
              <Boxes
                className={`mx-auto h-8 w-8 ${isLowStock ? "text-red-500" : "text-blue-500"}`}
              />
              <p className="mt-2 text-2xl font-bold">
                {Number(material.current_stock).toLocaleString("es-CL", {
                  maximumFractionDigits: 2,
                })}
              </p>
              <p className="text-sm text-muted-foreground">
                Stock Total ({material.unit})
              </p>
            </div>
          </CardContent>
        </Card>

        {isReturnableTotem ? (
          TOTEM_BUCKETS.map((bucket) => {
            const count = fleetSummary
              .filter((row) => bucket.statuses.includes(row.status))
              .reduce((sum, row) => sum + row.count, 0);
            return (
              <Card key={bucket.key} className="border bg-muted/40">
                <CardContent className="pt-6">
                  <div className="text-center">
                    <p className="mt-2 text-2xl font-bold">
                      {count.toLocaleString("es-CL")}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {bucket.label}
                    </p>
                  </div>
                </CardContent>
              </Card>
            );
          })
        ) : (
          <>
            {p1Stock !== null && (
              <Card className="border-emerald-200 bg-emerald-50">
                <CardContent className="pt-6">
                  <div className="text-center">
                    <Factory className="mx-auto h-8 w-8 text-emerald-600" />
                    <p className="mt-2 text-2xl font-bold text-emerald-700">
                      {Number(p1Stock).toLocaleString("es-CL", {
                        maximumFractionDigits: 2,
                      })}
                    </p>
                    <p className="text-sm text-emerald-600">
                      Planta 1 ({material.unit})
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}

            {p2Stock !== null && (
              <Card className="border-violet-200 bg-violet-50">
                <CardContent className="pt-6">
                  <div className="text-center">
                    <Factory className="mx-auto h-8 w-8 text-violet-600" />
                    <p className="mt-2 text-2xl font-bold text-violet-700">
                      {Number(p2Stock).toLocaleString("es-CL", {
                        maximumFractionDigits: 2,
                      })}
                    </p>
                    <p className="text-sm text-violet-600">
                      Planta 2 ({material.unit})
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}

            {material.minimum_stock_level !== null && (
              <Card>
                <CardContent className="pt-6">
                  <div className="text-center">
                    <AlertTriangle className="mx-auto h-8 w-8 text-amber-500" />
                    <p className="mt-2 text-2xl font-bold">
                      {material.minimum_stock_level}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Mínimo ({material.unit})
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>

      {isReturnableTotem && (
        <Alert>
          <Boxes className="h-4 w-4" />
          <AlertDescription>
            Este insumo es un tótem retornable: su stock físico se gestiona en
            Gestión de Tótems y se distribuye por estado (disponible, en
            cliente, lavado, con problema), no por planta.
          </AlertDescription>
        </Alert>
      )}

      {isLowStock && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            Stock bajo el nivel mínimo. Se recomienda reabastecer.
          </AlertDescription>
        </Alert>
      )}
    </>
  );
}
